import React, { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { motion } from 'framer-motion'
import { getUserProfile, UserProfile } from '../config/api'
import LawReference from '../components/LawReference'
import RaffleWidget from '../components/RaffleWidget'
import WinnersDisplay from '../components/WinnersDisplay'
import { ArrowLeft, LogOut, CalendarDays, Star, Coins, Clock, CheckCircle2, Wallet, ShoppingBag, Crown } from 'lucide-react'

interface AccountProps {
  onApplyClick: () => void
}

const Account: React.FC<AccountProps> = ({ onApplyClick }) => {
  const { user, isAuthenticated, logout } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [isLawReferenceOpen, setIsLawReferenceOpen] = useState(false)

  // Функция для получения роли пользователя
  const getUserRole = (roles: UserProfile['roles']) => {
    if (!roles || !Array.isArray(roles)) return null
    const targetRoles = ['Rookie', 'Relative', 'Family Member', 'High Staff', 'Trusted', 'Dep.Owner', 'Owner']
    const userRole = roles.find(role => role && role.name && targetRoles.includes(role.name))
    return userRole?.name || null
  }

  // Проверяем, является ли пользователь членом семьи
  const isFamilyMember = profile && profile.roles && getUserRole(profile.roles) !== null

  // Проверяем, является ли пользователь владельцем (Dep.Owner или Owner)
  const isOwner = profile && profile.roles && ['Dep.Owner', 'Owner'].includes(getUserRole(profile.roles) || '')

  // Функция для форматирования даты
  const formatFamilyDate = (dateString: string) => {
    if (!dateString) return '0 д.'
    
    try {
      const date = new Date(dateString.split('.').reverse().join('-'))
      const now = new Date()
      
      // Проверяем, что дата валидна
      if (isNaN(date.getTime())) {
        return '0 д.'
      }
      
      // Вычисляем разницу в днях
      const diffTime = Math.abs(now.getTime() - date.getTime())
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      // Вычисляем годы, месяцы и дни
      const years = Math.floor(diffDays / 365)
      const remainingDays = diffDays % 365
      const months = Math.floor(remainingDays / 30)
      const days = remainingDays % 30
      
      // Формируем строку
      let result = ''
      if (years > 0) {
        result += `${years} г. `
      }
      if (months > 0) {
        result += `${months} м. `
      }
      if (days > 0 || (years === 0 && months === 0)) {
        result += `${days} д.`
      }
      
      return result.trim()
    } catch (error) {
      console.error('Error formatting date:', error)
      return '0 д.'
    }
  }

  // Прогресс-строки отключены по запросу — вспомогательные расчеты не нужны

    // Современная карточка метрики
  const StatCard: React.FC<{
    icon: React.ReactNode
    label: string
    value: React.ReactNode
    gradientFrom: string
    gradientTo: string
  }> = ({ icon, label, value, gradientFrom, gradientTo }) => (
    <div 
      className="group relative overflow-hidden rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/10 bg-gradient-to-br from-dark-700/60 to-dark-800/60 backdrop-blur-xl"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute -top-14 -right-10 h-40 w-40 rounded-full bg-gradient-to-br ${gradientFrom} ${gradientTo} opacity-10 blur-2xl`} />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      </div>

      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${gradientFrom} ${gradientTo} flex items-center justify-center text-white shadow-inner shadow-black/20`}>{icon}</div>
      </div>

      <div className="mt-4 sm:mt-5">
        <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">{value}</div>
        <div className="text-gray-400 text-sm sm:text-base mt-1">{label}</div>
      </div>
    </div>
  )

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) return
      
      try {
        setLoading(true)
        const profileData = await getUserProfile(user.id)
        setProfile(profileData)
      } catch (err) {
        console.error('Profile loading error:', err)
        
        // При любой ошибке (включая failed to fetch, 404, пустой ответ и т.д.)
        // считаем что пользователь не является членом семьи
        setProfile(null)
      } finally {
        setLoading(false)
      }
    }

    loadProfile()
  }, [user?.id])

  // Если пользователь не авторизован, показываем сообщение
  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen pt-24 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </div>
    )
  }

  // Показываем загрузку
  if (loading) {
    return (
      <div className="min-h-screen pt-24 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          <p className="text-gray-400 mt-4">Загрузка профиля...</p>
        </div>
      </div>
    )
  }

  // Ошибки больше не показываем отдельно, они обрабатываются как "пользователь не найден"

  const avatarUrl = user.avatar
    ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`
    : undefined

  return (
    <div className="min-h-screen px-2 sm:px-4">
      {/* Элементы управления */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="sticky top-0 z-40 bg-dark-900/80 backdrop-blur-xl py-3 sm:py-4 mb-6 sm:mb-8"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between px-2 sm:px-0">
          {/* Кнопка назад */}
          <motion.button
            onClick={() => { window.location.hash = '' }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-12 h-12 sm:w-10 sm:h-10 rounded-xl bg-white/10 border border-white/20 text-gray-200 hover:text-white hover:bg-white/15 flex items-center justify-center"
            aria-label="Назад"
          >
            <ArrowLeft size={20} className="sm:w-[18px] sm:h-[18px]" />
          </motion.button>

          {/* Логотип */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex items-center space-x-2 sm:space-x-4 group cursor-pointer"
            onClick={() => { window.location.hash = '' }}
          >
            <div className="relative">
              <img 
                src="/content/hugo_logo.png" 
                alt="Hugo Family Logo" 
                className="h-8 sm:h-12 w-auto"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-lg sm:text-2xl font-bold text-orange-500">
                HUGO
              </span>
              <span className="text-xs sm:text-sm font-medium text-gray-300 -mt-1">
                Family
              </span>
            </div>
          </motion.div>

          {/* Кнопка выхода */}
          <motion.button
            onClick={logout}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-3 sm:px-4 py-3 sm:py-2 rounded-xl border border-white/20 text-gray-200 hover:text-white hover:bg-white/10 backdrop-blur-sm flex items-center space-x-2 sm:space-x-2"
            title="Выйти"
          >
            <LogOut size={18} className="sm:w-4 sm:h-4" />
            <span className="text-sm sm:text-base hidden sm:inline">Выйти</span>
          </motion.button>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8">
        {/* Заголовок */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >


        </motion.div>

        {/* Профиль */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative overflow-hidden bg-gradient-to-br from-dark-700/60 to-dark-800/60 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-white/10 shadow-2xl"
        >
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-16 -right-12 h-48 w-48 rounded-full bg-gradient-to-br from-orange-500 to-red-500 opacity-10 blur-2xl"></div>
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"></div>
          </div>
          <div className="flex items-center space-x-4 sm:space-x-6">
            {/* Аватар */}
            <motion.div
              whileHover={{ scale: 1.05, rotate: 5 }}
              className="relative group flex-shrink-0"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden shadow-lg">
                {avatarUrl ? (
                  <img 
                    src={avatarUrl} 
                    alt={user.username} 
                    className="w-full h-full object-cover rounded-xl"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                      e.currentTarget.nextElementSibling?.classList.remove('hidden')
                    }}
                  />
                ) : null}
                <div className={`w-full h-full rounded-xl bg-dark-700 flex items-center justify-center text-xl sm:text-2xl font-bold text-white ${avatarUrl ? 'hidden' : ''}`}>
                  {user.username?.charAt(0).toUpperCase() || 'U'}
                </div>
              </div>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </motion.div>

            {/* Информация пользователя */}
            <div className="flex-1 min-w-0">
              <motion.h2 
                className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-1 sm:mb-2 truncate"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {profile?.user?.display_name || user.global_name || user.username || 'Пользователь'}
              </motion.h2>
              <motion.div 
                className="text-gray-400 text-sm sm:text-base mb-2 truncate"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                @{user.username}
              </motion.div>
              {profile && isFamilyMember && (
                <div className="flex items-center space-x-1">
                  <motion.div 
                    className="inline-block bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 text-orange-400 px-2 sm:px-3 py-1 rounded-full text-xs font-medium"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    {getUserRole(profile.roles) || 'Member'}
                  </motion.div>
                  {isOwner && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 }}
                      className="text-yellow-400"
                    >
                      <Crown size={16} className="sm:w-4 sm:h-4" />
                    </motion.div>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>



        {/* Статистика или окно подачи заявки */}
        {profile && isFamilyMember && profile.stats ? (
          <div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <StatCard 
                icon={<CalendarDays size={18} />}
                label="В семье"
                value={profile.family_date ? formatFamilyDate(profile.family_date) : '0 д.'}
                gradientFrom="from-orange-500" 
                gradientTo="to-red-500"
              />

              <StatCard 
                icon={<Star size={18} />}
                label="Баллы"
                value={(profile.stats.points || 0).toLocaleString()}
                gradientFrom="from-blue-500" 
                gradientTo="to-cyan-500"
              />

              <StatCard 
                icon={<Coins size={18} />}
                label="Hugo Coin'ов"
                value={(profile.stats.coins || 0).toLocaleString()}
                gradientFrom="from-yellow-500" 
                gradientTo="to-orange-500"
              />

              <StatCard 
                icon={<Clock size={18} />}
                label="Время в войсе"
                value={profile.stats.voice_time_human || '0ч 0м'}
                gradientFrom="from-green-500" 
                gradientTo="to-emerald-500"
              />

              <StatCard 
                icon={<CheckCircle2 size={18} />}
                label="Выполнено контрактов"
                value={(profile.stats.contracts_completed || 0).toLocaleString()}
                gradientFrom="from-purple-500" 
                gradientTo="to-pink-500"
              />

              <StatCard 
                icon={<Wallet size={18} />}
                label="Заработано на контрактах"
                value={(profile.stats.contracts_earned || 0).toLocaleString()}
                gradientFrom="from-teal-400" 
                gradientTo="to-emerald-500"
              />
            </div>
          </div>
        ) : (
                            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-gradient-to-br from-dark-800/80 to-dark-900/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 border border-white/10 shadow-2xl text-center"
              >
                <div className="max-w-lg mx-auto">
                  <motion.div 
                    className="text-6xl sm:text-8xl mb-4 sm:mb-6"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                  >
                    🏠
                  </motion.div>
                  <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-3 sm:mb-4">
                    Вы не являетесь членом семьи
                  </h3>
                  <p className="text-gray-400 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed">
                    Для доступа к личному кабинету и статистике необходимо подать заявку на вступление в Hugo Family
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white px-6 sm:px-10 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-semibold text-base sm:text-lg hover:from-orange-600 hover:via-red-600 hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-xl"
                    onClick={onApplyClick}
                  >
                    Подать заявку
                  </motion.button>
                </div>
              </motion.div>
        )}

        {/* Кнопки действий */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center"
        >
          <div className="grid grid-cols-3 gap-3 sm:gap-6">
            {/* Памятка by HUGO */}
            <motion.button
              whileHover={{ scale: 1.02, y: -4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsLawReferenceOpen(true)}
              className="group relative overflow-hidden bg-gradient-to-br from-blue-500/20 to-blue-600/30 backdrop-blur-xl border border-blue-400/30 text-white px-4 sm:px-6 lg:px-8 py-4 sm:py-5 rounded-2xl sm:rounded-3xl font-semibold text-base sm:text-lg hover:border-blue-400/50 transition-all duration-200 shadow-2xl hover:shadow-blue-500/25 flex items-center gap-3 sm:gap-4"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
              <div className="relative z-10 flex items-center gap-3 sm:gap-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-lg">
                  <span className="text-lg sm:text-xl">⚖️</span>
                </div>
                <div className="text-left">
                  <div className="font-bold text-base sm:text-lg">Памятка</div>
                  <div className="text-xs sm:text-sm text-blue-200">by HUGO</div>
                </div>
              </div>
            </motion.button>
            
            {/* HUGO Img */}
            <motion.a
              href="https://img.hugoinc.family/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.02, y: -4 }}
              whileTap={{ scale: 0.98 }}
              className="group relative overflow-hidden bg-gradient-to-br from-purple-500/20 to-purple-600/30 backdrop-blur-xl border border-purple-400/30 text-white px-4 sm:px-6 lg:px-8 py-4 sm:py-5 rounded-2xl sm:rounded-3xl font-semibold text-base sm:text-lg hover:border-purple-400/50 transition-all duration-200 shadow-2xl hover:shadow-purple-500/25 flex items-center gap-3 sm:gap-4"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
              <div className="relative z-10 flex items-center gap-3 sm:gap-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center shadow-lg">
                  <span className="text-lg sm:text-xl">📁</span>
                </div>
                <div className="text-left">
                  <div className="font-bold text-base sm:text-lg">HUGO Img</div>
                  <div className="text-xs sm:text-sm text-purple-200">Галерея</div>
                </div>
              </div>
            </motion.a>

            {/* Магазин */}
            <motion.button
              onClick={() => { window.location.hash = '#shop' }}
              whileHover={{ scale: 1.02, y: -4 }}
              whileTap={{ scale: 0.98 }}
              className="group relative overflow-hidden bg-gradient-to-br from-emerald-500/20 to-emerald-600/30 backdrop-blur-xl border border-emerald-400/30 text-white px-4 sm:px-6 lg:px-8 py-4 sm:py-5 rounded-2xl sm:rounded-3xl font-semibold text-base sm:text-lg hover:border-emerald-400/50 transition-all duration-200 shadow-2xl hover:shadow-emerald-500/25 flex items-center gap-3 sm:gap-4"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
              <div className="relative z-10 flex items-center gap-3 sm:gap-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg">
                  <ShoppingBag size={16} className="sm:w-[18px] sm:h-[18px]" />
                </div>
                <div className="text-left">
                  <div className="font-bold text-base sm:text-lg">Магазин</div>
                  <div className="text-xs sm:text-sm text-emerald-200">Товары</div>
                </div>
              </div>
            </motion.button>
          </div>
        </motion.div>

        {/* Розыгрыш - только для членов семьи */}
        {profile && isFamilyMember && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-6 sm:mt-8"
          >
            <RaffleWidget
              discordId={user.id}
            />
          </motion.div>
        )}

        {/* Победители розыгрышей - показываем всем */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-6 sm:mt-8"
        >
          <WinnersDisplay />
        </motion.div>

        {/* Компонент памятки с законами */}
        <LawReference 
          isOpen={isLawReferenceOpen} 
          onClose={() => setIsLawReferenceOpen(false)} 
        />
      </div>
    </div>
  )
}

export default Account

